import { useContext, useState } from "react";
import { Box, Button, CircularProgress, TextField } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Form from "./style";
import toast from "react-hot-toast";
import schema, { validationEmail } from "./schema";
import { AppContext } from "../../scripts/context/App";
import { Link } from "react-router-dom";

const initErrorsInputs = {
  email: false,
  password: false,
  password_confirmation: false,
  reset_token: false,
}

export default function ResetPasswordForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams({
    email: "",
    reset_token: "",
  });
  const { setState } = useContext(AppContext);
  const [userInformation, setUserInformation] = useState({
    email: searchParams.get("email") || "",
    password: "",
    password_confirmation: "",
    reset_token: searchParams.get("reset_token") || "",
  });
  const [errors, setErrors] = useState(initErrorsInputs);
  const [loading, setLoading] = useState(false);

  const handelChanges = (event: any) => {
    const { name, value } = event.target;

    setUserInformation({ ...userInformation, [name]: value });
  };

  const handelSubmit = (event: any) => {
    event.preventDefault();

    schema
      .validate(userInformation, { abortEarly: false })
      .then(async () => {
        try {
          setErrors(initErrorsInputs);
          setLoading(true);
          const { data } = await axios.post(
            "saveRecoveryPassword",
            userInformation
          );

          localStorage.setItem("token", data.token);
          axios.defaults.headers.common["Authorization"] =
            data.token.includes("Bearer")
              ? data.token
              : `Bearer ${data.token}`;
          try {
            const roles_and_permissions = await Promise.all([
              data.user.permissions.includes("view_system_role") ? axios.get("/roles/get-roles", {
                params: {
                  getAll: 1,
                },
              }) : Promise.resolve(null),
              axios.get("/roles/get-permissions"),
            ]);

            setState({
              user: data.user,
              roles_permissions: roles_and_permissions[0]?.data || [],
              permissions: roles_and_permissions[1].data.data || [],
            });
            setLoading(false);
          } catch (error) {
            console.log(error);
          }

          data.user.permissions.includes("view_claimers") ? navigate("/dashboard/claimer-management") : navigate("/");
        } catch (error: any) {
          toast.error(
            error.response.data.message ||
            "Something went wrong, please try again"
          );

          console.log(error);
          setLoading(false);
        }
      })
      .catch((error) => {
        setErrors(
          error.inner.reduce((acc: any, err: any) => {
            acc[err.path] = err.message;
            return acc;
          }, {})
        );
      });

    setLoading(false);
  };

  const sendRecoveryPassword = async (event: any) => {
    event.preventDefault();

    try {
      setErrors(initErrorsInputs);
      await validationEmail.validate(userInformation, { abortEarly: false });
      const { data } = await axios.post("sendRecoveryPassword", { email: userInformation.email });

      toast.success(
        data.message || "We have sent you an email with instructions to reset your password"
      );
      setUserInformation({ ...userInformation, email: "" });
    } catch (error: any) {
      if (error.inner) {
        return setErrors(error.inner.reduce((acc: any, err: any) => {
          acc[err.path] = err.message;
          return acc;
        }, {}));
      }

      if (error.response.data.errors) {
        toast.error(error.response.data.errors.email[0]);
      } else {
        toast.error(
          error.response.data.message || "Something went wrong. Please try again later or contact support."
        )
      }
    }
  }

  if (!searchParams.get("email")) {
    return (
      <>
        <Form onSubmit={sendRecoveryPassword}>
          <TextField
            inputProps={{
              style: { color: "white" },
              autoComplete: "username",
              className: "dark-input-label",
            }}
            size="small"
            InputLabelProps={{ style: { color: "rgb(255 255 255 / 60%)" } }}
            value={userInformation.email}
            onChange={handelChanges}
            name="email"
            id="email"
            label="Email"
            variant="standard"
            type="text"
            error={Boolean(errors.email)}
            helperText={errors.email}
          />

          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={loading}
            startIcon={
              loading && (
                <CircularProgress
                  sx={{ width: "15px !important", height: "15px !important" }}
                />
              )
            }
          >
            Request
          </Button>
        </Form>
        <Box
          sx={{
            textAlign: "center",
            mt: "1rem",
            a: {
              color: "#fff",
              textDecoration: "none",
              fontSize: "13px",
              ":hover": {
                textDecoration: "underline",
              },
            },
          }}
        >
          <Link to="/login">Back to Login page</Link>
        </Box>
      </>
    )
  }

  return (
    <>
      <Form onSubmit={handelSubmit}>
        <TextField
          inputProps={{
            style: { color: "white" },
            autoComplete: "username",
            className: "dark-input-label",
          }}
          size="small"
          InputLabelProps={{ style: { color: "rgb(255 255 255 / 60%)" } }}
          value={userInformation.password}
          onChange={handelChanges}
          name="password"
          id="password"
          label="New Password"
          variant="standard"
          type="password"
          error={Boolean(errors.password)}
          helperText={errors.password}
        />
        <TextField
          inputProps={{
            style: { color: "white" },
            autoComplete: "username",
            className: "dark-input-label",
          }}
          size="small"
          InputLabelProps={{ style: { color: "rgb(255 255 255 / 60%)" } }}
          value={userInformation.password_confirmation}
          onChange={handelChanges}
          name="password_confirmation"
          id="password_confirmation"
          label="Password Confirmation"
          variant="standard"
          type="password"

          error={Boolean(errors.password_confirmation)}
          helperText={errors.password_confirmation}
        />
        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={loading}
          startIcon={
            loading && (
              <CircularProgress
                sx={{ width: "15px !important", height: "15px !important" }}
              />
            )
          }
        >
          Reset
        </Button>
      </Form>
      <Box
        sx={{
          textAlign: "center",
          mt: "1rem",
          a: {
            color: "#fff",
            textDecoration: "none",
            fontSize: "13px",
            ":hover": {
              textDecoration: "underline",
            },
          },
        }}
      >
        <Link to="/login">Login</Link>
      </Box>
    </>
  );
}
